export type RepeatBy = "beneficiary" | "article";

/** Title fields that always belong to the article of the current repeated block. */
const ARTICLE_REPEAT_VARIABLE_KEYS = new Set(["articleTitle", "articleReleaseDate", "articleFormat", "ean13"]);

export type RepeatableArticle = {
    id: number;
    name: string;
};

export const articlesInWorkOrder = <T extends { id: number }>(articles: T[]): T[] =>
    [...articles].sort((left, right) => left.id - right.id);

export const articleRepeatVariableKey = (index: string | number, variableName: string) =>
    `articles.${index}.${variableName}`;

export const beneficiaryRepeatVariableKey = (index: string | undefined, variableName: string) =>
    `beneficiaries.${index}.${variableName}`;

export const isArticleRepeatStorageKey = (key: string) => /^articles\.\d+\./.test(key);

export const articleRepeatIndex = (key: string) => {
    const match = /^articles\.(\d+)\./.exec(key);
    if (!match) return undefined;
    return Number(match[1]);
};

export const isArticleRepeatVariableKey = (variableName: string) => ARTICLE_REPEAT_VARIABLE_KEYS.has(variableName);

/**
 * Key used to fill a variable that sits inside a repeated block.
 * Article blocks scope title fields (and any key gathered for that article).
 * Other variables in the block stay on the agreement root.
 * Beneficiary blocks keep the historical `beneficiaries.{index}.{name}` key, including an empty index.
 */
export const resolveRepeatableVariableKey = ({
    repeatBy,
    index,
    variableName,
    articleValues,
}: {
    repeatBy: RepeatBy;
    index: string | undefined;
    variableName: string;
    articleValues?: Record<string, string>;
}): string | undefined => {
    if (repeatBy !== "article") {
        return beneficiaryRepeatVariableKey(index, variableName);
    }
    if (index == null || index === "") return undefined;
    const key = articleRepeatVariableKey(index, variableName);
    if (isArticleRepeatVariableKey(variableName) || variableName.startsWith("meta:")) return key;
    if (articleValues && Object.prototype.hasOwnProperty.call(articleValues, key)) return key;
    return undefined;
};
